import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { agentsService } from '../services/agentsService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Breadcrumb, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbList, 
  BreadcrumbPage, 
  BreadcrumbSeparator 
} from '../components/ui/breadcrumb';
import { Phone, PhoneOff, Loader2, AlertCircle } from 'lucide-react';
import { StarBorder } from '../components/ui/star-border';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useAnalytics } from '../hooks/useAnalytics';
import { useElevenLabsConversation } from '../hooks/useElevenLabsConversation';

const TalkToAgent = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Analytics hooks
  const { trackPageView, trackButtonClick } = useAnalytics();
  
  // ElevenLabs conversation hook
  const {
    connectionState,
    error: conversationError,
    conversationId,
    transcript,
    callDuration,
    formattedDuration,
    audioLevel,
    startConversation,
    endConversation,
    isConnected,
    isConnecting,
    isIdle,
    isError: isConversationError,
    canStartCall
  } = useElevenLabsConversation(agentId, agent);



  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setIsLoading(true);
        const response = await agentsService.getAgentById(agentId);
        
        // Fix: Use response directly since the agent data is not nested in response.data
        const agentData = response;
        setAgent(response);
        
        // Track page view with agent details
        trackPageView('talk_to_agent', {
          agent_id: agentId,
          agent_name: agentData?.name,
          entry_point: 'direct_navigation',
          has_voice_config: !!(agentData?.conversation_config?.tts?.voice_id)
        });
        
      } catch (err) {
        setError('Failed to load agent details');
        
        // Track error
        trackPageView('talk_to_agent_error', {
          agent_id: agentId,
          error_message: err.message
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (agentId) {
      fetchAgent();
    }
  }, [agentId, trackPageView]);

  // Cleanup: End call when user leaves the page
  useEffect(() => {
    return () => {
      if (isConnected) {
        endConversation();
      }
    };
  }, [isConnected, endConversation]);

  // Handle conversation actions
  const handleStartConversation = async () => {
    trackButtonClick('start_conversation', 'talk_to_agent', {
      agent_id: agentId,
      agent_name: agent?.name,
      voice_id: agent?.conversation_config?.tts?.voice_id
    });
    
    await startConversation();
  };

  const handleEndConversation = async () => {
    trackButtonClick('end_conversation', 'talk_to_agent', {
      agent_id: agentId,
      conversation_id: conversationId,
      call_duration: callDuration
    });
    
    await endConversation();
  };


  // Audio level visualization (0-5 bars)
  const getAudioBars = () => {
    const level = Math.ceil(audioLevel * 5);
    return Array(5).fill(0).map((_, i) => i < level);
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto p-8 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card className="border border-border bg-card shadow-none">
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-12 w-32" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="border border-border bg-card shadow-none max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-destructive mb-4">
              Error loading agent
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/agents')} className="bg-primary hover:bg-primary/90">
              Back to Agents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header with enhanced styling - copied from AgentDetails */}
      <div className="bg-sidebar border-b border-border/60 sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-8">
          {/* Left side - Breadcrumb aligned to left edge */}
          <div className="flex items-center">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-sm font-medium hover:text-primary cursor-pointer text-muted-foreground"
                  >
                    <button onClick={() => navigate('/agents')}>
                      Agents
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-muted-foreground" />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-sm font-medium hover:text-primary cursor-pointer text-muted-foreground"
                  >
                    <button onClick={() => navigate(`/agents/${agentId}`)}>
                      {agent?.name || 'Loading...'}
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-muted-foreground" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-sm font-medium text-card-foreground">
                    Talk to agent
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
      </div>

      {/* Main content area — full height two-section layout */}
      <div className="max-w-3xl mx-auto flex flex-col px-6 pt-6 pb-8" style={{ height: 'calc(100vh - 4rem)' }}>
        {/* Alerts */}
        {conversationError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Conversation Error</AlertTitle>
            <AlertDescription>{conversationError}</AlertDescription>
          </Alert>
        )}
        {agent && !agent.conversation_config?.tts?.voice_id && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Voice Not Configured</AlertTitle>
            <AlertDescription>
              This agent doesn't have a voice configured. Voice conversations may not work properly.
            </AlertDescription>
          </Alert>
        )}

        {/* Call Controls Panel */}
        <div className="flex flex-col items-center py-6 gap-4 shrink-0">
          {/* Agent avatar + name */}
          <div className="flex flex-col items-center gap-2">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center text-2xl font-bold text-muted-foreground">
              {(agent?.name || 'A').charAt(0).toUpperCase()}
            </div>
            <h2 className="text-xl font-semibold">{agent?.name || 'Agent'}</h2>
          </div>

          {/* Status + duration row */}
          <div className="flex items-center gap-3">
            {isIdle && (
              <span className="text-sm text-muted-foreground">Ready to call</span>
            )}
            {isConnecting && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Calling...
              </span>
            )}
            {isConnected && (
              <>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Connected
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">{formattedDuration}</span>
                {/* Audio level */}
                <div className="flex items-end gap-px h-4">
                  {getAudioBars().map((active, i) => (
                    <div
                      key={i}
                      className={`w-0.5 rounded-full transition-all duration-100 ${active ? 'bg-emerald-500' : 'bg-muted'}`}
                      style={{ height: `${6 + i * 3}px` }}
                    />
                  ))}
                </div>
              </>
            )}
            {connectionState === 'disconnected' && (
              <span className="text-sm text-muted-foreground">Call ended</span>
            )}
            {isConversationError && (
              <span className="text-sm text-red-500">Connection failed</span>
            )}
          </div>

          {/* Call button */}
          {!isConnected && !isConnecting ? (
            <StarBorder
              as="button"
              onClick={handleStartConversation}
              disabled={!canStartCall}
              className="text-nowrap"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <Phone className="w-4 h-4" />
                Start Conversation
              </span>
            </StarBorder>
          ) : (
            <button
              onClick={handleEndConversation}
              className="h-12 w-12 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 transition-all flex items-center justify-center shadow-lg"
              title="End Conversation"
            >
              <PhoneOff className="h-5 w-5 text-white" />
            </button>
          )}
        </div>

        {/* Transcript Chat Area */}
        {transcript.length > 0 && (
          <div className="flex flex-col flex-1 min-h-0 mt-2">
            <div className="border-t border-border" />
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {transcript.map((item, index) => (
                <div key={index} className={`flex ${item.speaker === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed ${
                    item.speaker === 'user'
                      ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm'
                      : 'bg-muted text-foreground rounded-2xl rounded-bl-sm'
                  }`}>
                    {item.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TalkToAgent;
